/**
 * Bulk-self-schedule prerequisite step.
 *
 * Mounts BEFORE the standard 4-step wizard. Two responsibilities:
 *   1. Pick the interview (`ats_job_stage_action`) to schedule when
 *      the stage exposes more than one. When there's only one,
 *      auto-select it.
 *   2. Categorise the selected applications into eligible / no-email /
 *      already-scheduled and surface the latter two as warnings.
 *
 * Auto-advances (calls `onResolve` without user interaction) when:
 *   - exactly one stage action exists, AND
 *   - every selected candidate is eligible (no warnings to show)
 *
 * Mirrors `KInterviewScheduler/InterviewToSchedule`. Layout is the
 * same shape — different chrome (the wizard's `KDrawer` wraps it
 * in full-screen mode) — but rules are pinned in `categorise.ts`
 * tests so behaviour can't drift.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Icon, Spinner } from '@chakra-ui/react';
import { Avatar, Button, Select } from '@kula-ai/components';
import { CalendarCheck } from '@/assets/icons/interview';
import { FeaturedIcon } from '@/assets/icons/ats';
import { KText } from '@/ui-library/components/text';
import { categoriseApplications, shouldAutoAdvance } from './categorise';
import { useBulkSelfScheduleInterviewStatus } from '@/api/useBulkSelfScheduleInterviewStatus';
import type {
  BulkSelfScheduleApplication,
  BulkSelfScheduleEntryProps,
  BulkSelfSchedulePrerequisiteResolution,
  BulkSelfScheduleStageAction,
} from './types';

const fullName = (app: BulkSelfScheduleApplication): string => {
  const c = app.contact;
  return (
    c.full_name ?? `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() ?? ''
  );
};

const candidateAvatarSrc = (
  app: BulkSelfScheduleApplication
): string | undefined =>
  app.contact.avatar_thumb_url ??
  app.contact.avatar_url ??
  app.contact.image_url;

interface BulkSelfSchedulePrerequisiteOverlayProps {
  bulkSelfScheduleProps: BulkSelfScheduleEntryProps;
  onResolve: (_resolution: BulkSelfSchedulePrerequisiteResolution) => void;
  onCancel: () => void;
}

export const BulkSelfSchedulePrerequisiteOverlay = ({
  bulkSelfScheduleProps,
  onResolve,
  onCancel,
}: BulkSelfSchedulePrerequisiteOverlayProps) => {
  const {
    selectedApplications,
    unSelectedApplications,
    jobStageId,
    jobStatus,
  } = bulkSelfScheduleProps;

  const selectedApplicationIds = useMemo(
    () => selectedApplications?.map((a) => a.id),
    [selectedApplications]
  );

  const { data: status, isLoading } = useBulkSelfScheduleInterviewStatus({
    selectedApplicationIds,
    unSelectedApplicationIds: unSelectedApplications,
    jobStageId,
    jobStatus,
  });

  const stageActions: BulkSelfScheduleStageAction[] = useMemo(
    () => status?.interview_job_stage_actions ?? [],
    [status]
  );

  // The `interview_status` response's `ats_applications.contact`
  // intentionally omits `email` (server-side performance: it only
  // needs ids + names for categorisation). The parent table's
  // `selectedApplications` carries the full contact row including
  // `email`, so we merge here. Without this the slot-selection
  // email's "To" defaults to '' even when the contact has an email
  // on file → validation fails the user could not have foreseen.
  const applicationIdToEmail = useMemo(() => {
    const map = new Map<number, string>();
    for (const app of selectedApplications ?? []) {
      const email = app.contact?.email?.trim();
      if (typeof app.id === 'number' && email) map.set(app.id, email);
    }
    return map;
  }, [selectedApplications]);

  // Two states: what the user picked, and what's auto-selected when
  // only one option exists. Deriving from `stageActions` instead of
  // mirroring it via setState-in-effect avoids the cascading-render
  // anti-pattern (react-hooks/set-state-in-effect).
  const [userPickedActionId, setUserPickedActionId] = useState<number | null>(
    null
  );
  const selectedAction: BulkSelfScheduleStageAction | null = useMemo(() => {
    if (userPickedActionId !== null) {
      return stageActions.find((a) => a.id === userPickedActionId) ?? null;
    }
    return stageActions.length === 1 ? stageActions[0] : null;
  }, [stageActions, userPickedActionId]);

  // Categorise from the API response, then enrich with the table's
  // selectedApplications data so candidates without an email on file
  // move to the no-email bucket even if the API didn't flag them.
  // The API's `interview_status` response omits `email` from
  // `ats_applications.contact`, so this client-side join is also
  // what populates the anchor's email at resolution time.
  const result = useMemo(() => {
    const base = categoriseApplications(status, selectedAction);
    const eligibleWithEmail: typeof base.eligible = [];
    const eligibleWithoutEmail: typeof base.eligible = [];
    for (const app of base.eligible) {
      if (applicationIdToEmail.has(app.id)) {
        eligibleWithEmail.push(app);
      } else {
        eligibleWithoutEmail.push(app);
      }
    }
    return {
      eligible: eligibleWithEmail,
      noEmail: [...base.noEmail, ...eligibleWithoutEmail],
      alreadyScheduled: base.alreadyScheduled,
    };
  }, [status, selectedAction, applicationIdToEmail]);

  // Auto-advance once the data + categorisation settle into a "no
  // user choice required" state. The ref guards against re-firing
  // when the parent re-renders us and the input data is unchanged.
  const autoAdvancedRef = useRef(false);
  useEffect(() => {
    if (autoAdvancedRef.current) return;
    if (!status || !selectedAction) return;
    if (!shouldAutoAdvance(status, result)) return;
    autoAdvancedRef.current = true;
    onResolve(
      buildResolution(
        jobStageId,
        selectedAction.id,
        result,
        applicationIdToEmail
      )
    );
  }, [
    status,
    selectedAction,
    result,
    jobStageId,
    onResolve,
    applicationIdToEmail,
  ]);

  const handleStart = () => {
    if (!selectedAction) return;
    if (result.eligible.length === 0) return;
    onResolve(
      buildResolution(
        jobStageId,
        selectedAction.id,
        result,
        applicationIdToEmail
      )
    );
  };

  if (isLoading) {
    return (
      <Flex h="full" w="full" alignItems="center" justifyContent="center">
        <Spinner color="primary-800" />
      </Flex>
    );
  }

  return (
    <Flex
      h="full"
      w="full"
      alignItems="center"
      justifyContent="center"
      flexDir="column"
      px="6"
      py="8"
    >
      <Flex
        flexDir="column"
        gap="24px"
        alignItems="center"
        flex="2"
        justifyContent="flex-end"
        maxW="480px"
        w="100%"
      >
        <Icon as={CalendarCheck} w="auto" h="200px" maxW="100%" />
        <KText variant="heading_2">
          {stageActions.length === 1 ? 'Interview' : 'Select the interview'} to
          schedule
        </KText>
        <Flex w="100%" justifyContent="center" flexDir="column" gap="12px">
          <Select
            options={stageActions.map((a) => ({
              // The API has historically returned the display string
              // under `label`; some payloads use `name`. Read both
              // before falling back to the id so the dropdown shows
              // the actual interview name (matching legacy KSelect's
              // default `getOptionLabel = option.label`).
              label: a.label ?? a.name ?? `Interview ${a.id}`,
              value: String(a.id),
            }))}
            value={selectedAction ? String(selectedAction.id) : ''}
            placeholder="Select interview"
            disabled={stageActions.length === 1}
            onChange={(value) => {
              if (typeof value !== 'string' || !value) return;
              const next = stageActions.find((a) => String(a.id) === value);
              if (next) setUserPickedActionId(next.id);
            }}
          />
          <Flex justifyContent="center" gap="12px">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              disabled={!selectedAction || result.eligible.length === 0}
              onClick={handleStart}
            >
              Start self-schedule
            </Button>
          </Flex>
        </Flex>
      </Flex>

      <Flex
        flex="1"
        justifyContent="flex-end"
        pb="24px"
        flexDir="column"
        my="8px"
        gap="8px"
        maxW="650px"
        w="100%"
      >
        {result.noEmail.length > 0 && (
          <InvalidCandidatesBanner
            candidates={result.noEmail}
            tone="warning"
            context={
              "don't have an associated email address and will not receive the self-scheduling link"
            }
          />
        )}
        {result.alreadyScheduled.length > 0 && (
          <InvalidCandidatesBanner
            candidates={result.alreadyScheduled}
            tone="info"
            context={
              'have already scheduled or completed this interview and will not receive the self-schedule link'
            }
          />
        )}
      </Flex>
    </Flex>
  );
};

function buildResolution(
  jobStageId: number,
  stageActivityId: number,
  result: ReturnType<typeof categoriseApplications>,
  applicationIdToEmail: Map<number, string>
): BulkSelfSchedulePrerequisiteResolution {
  const anchor = result.eligible[0];
  // `jobStageId` may arrive as a string when callers forward
  // `useParams()`/`queryParams` values without coercion. Normalise here
  // so downstream consumers (`useInterviewPlan`, calendar fetch, save
  // payload) get the numeric form they're typed for.
  const numericStageId = Number(jobStageId);
  // Anchor email comes from the parent table's `selectedApplications`
  // map — `interview_status.ats_applications.contact` doesn't include
  // `email` server-side, so reading `anchor.contact.email` directly
  // would always be `''` and the slot-email step would default `to`
  // to empty.
  const anchorEmail =
    applicationIdToEmail.get(anchor.id) ?? anchor.contact.email ?? '';
  return {
    applicationIds: result.eligible.map((a) => a.id),
    stageActivityId,
    stageId: numericStageId,
    anchorApplicationId: anchor.id,
    anchorContactId: anchor.contact.id,
    anchorCandidateEmail: anchorEmail,
    eligibleApplications: result.eligible,
    noEmailApplications: result.noEmail,
    alreadyScheduledApplications: result.alreadyScheduled,
  };
}

interface InvalidCandidatesBannerProps {
  candidates: BulkSelfScheduleApplication[];
  tone: 'warning' | 'info';
  context: string;
}

const InvalidCandidatesBanner = ({
  candidates,
  tone,
  context,
}: InvalidCandidatesBannerProps) => {
  const { bg, color } =
    tone === 'warning'
      ? { bg: 'pastel-1-light', color: 'pastel-1-dark' }
      : { bg: 'pastel-6-light', color: 'pastel-6-dark' };
  const visible = candidates.slice(0, 3);
  const hidden = candidates.length - visible.length;
  return (
    <Flex bg={bg} borderRadius="sm" p="16px" flexDir="column">
      <Flex gap="11px">
        <Icon as={FeaturedIcon} w="38px" h="38px" color={color} />
        <Flex flexDir="column" gap="8px" w="calc(100% - 49px)">
          <KText color={color}>
            <strong>
              {candidates.length === 1
                ? '1 candidate'
                : `${candidates.length} candidates`}
            </strong>{' '}
            {context}
          </KText>
          <Flex gap="6px" w="100%" flexWrap="nowrap" overflow="hidden">
            {visible.map((app) => (
              <CandidatePill key={app.id} app={app} />
            ))}
            {hidden > 0 && (
              <Box
                bg="white"
                w="33px"
                h="33px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="6px"
              >
                <KText color="secondary-700" fontWeight="550" fontSize="12px">
                  +{hidden}
                </KText>
              </Box>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

const CandidatePill = ({ app }: { app: BulkSelfScheduleApplication }) => {
  const name = fullName(app);
  return (
    <Flex
      alignItems="center"
      gap="8px"
      bg="white"
      p="6px 12px"
      maxW="fit-content"
      borderRadius="sm"
      whiteSpace="nowrap"
    >
      <Avatar size="sm" name={name} src={candidateAvatarSrc(app)} />
      <KText size="sm-medium" maxW="120px" isTruncated>
        {name}
      </KText>
    </Flex>
  );
};
